export default class Error {
    static fromResponse(response) {
        const error = new Error();

        error.status = response.status;
        error.details = [];

        if (response.data.error) {
            error.message = response.data.error.message;
            error.key = response.data.error.key;

            if (response.data.error.details) {
                error.details = Error.parseDetails(response.data.error.details);
            }
        }

        return error;
    }

    static parseDetails(rawDetails) {
        if (!Array.isArray(rawDetails) || rawDetails.length === 0) {
            return [];
        }

        // Validation errors serialize details as a list of lists of {key, value};
        // other errors (e.g. objectNotFound) serialize a single flat list of {key, value}.
        const isNested = Array.isArray(rawDetails[0]);
        const groups = isNested ? rawDetails : [rawDetails];

        const details = [];
        groups.forEach((group) => {
            if (!Array.isArray(group) || group.length === 0) {
                return;
            }

            const errorDetail = [];
            group.forEach((detail) => errorDetail[detail.key] = detail.value);
            details.push(errorDetail);
        });

        return details;
    }

    static async fromBlob(response) {
        const error = new Error();

        error.status = response.status;
        error.details = [];

        const text = await response.data.text();
        const data = JSON.parse(text);

        if (data?.error) {
            error.message = data.error.message;
            error.key = data.error.key;

            if (data.error.details) {
                error.details = Error.parseDetails(data.error.details);
            }
        }

        return error;
    }

    forField(field) {
        if (this.isEmpty()) {
            return false;
        }

        return this.details.filter((detail) => detail.attributeName === field).length > 0;
    }

    add(field, message) {
        if (this.isEmpty()) {
            this.details = [];
        }

        this.details.push({
            attributeName: field,
            constraintMessage: message,
        });
    }

    clear(field) {
        if (this.isEmpty()) {
            return;
        }
        this.details = this.details.filter((detail) => detail.attributeName !== field);
    }

    isEmpty() {
        return !this.hasOwnProperty('details') || this.details.length < 1;
    }

    messageForField(field, distinct) {
        const messagesForField = this.messagesForField(field, distinct);
        if (messagesForField !== null) {
            return messagesForField.join(' ');
        }
        return null;
    }

    /**
     * Every violation message, whichever field it belongs to. A toast cannot ask messagesForField(): it does
     * not know what the server objected to, and the property path is a DTO name that no form field is bound
     * to. Without this the reason is discarded and the user is told only that saving failed.
     */
    allMessages(distinct = true) {
        if (this.isEmpty()) {
            return [];
        }

        const messages = this.details
            .map((detail) => detail.constraintMessage)
            .filter((message) => !!message);

        return distinct ? messages.filter(this.distinct) : messages;
    }

    messagesForField(field, distinct) {
        if (this.isEmpty()) {
            return null;
        }

        let messagesForField = this.details
            .filter((detail) => detail.attributeName === field)
            .map((detail) => detail.constraintMessage)
            .filter((msg) => !!msg);

        if (messagesForField.length === 0) {
            return null;
        }

        if (distinct) {
            messagesForField = messagesForField.filter(this.distinct);
        }

        return messagesForField;
    }

    distinct(value, index, self) {
        return self.indexOf(value) === index;
    }

    errorAtIndex(field, index) {
        const regex = new RegExp(`^${field}\\[${index}\\]\\.(.+)`);
        return this._errorByRegex(regex);
    }

    collectionErrorAtIndex(field, index) {
        const regex = new RegExp(`^${field}\\[${index}\\]\\[(.+)\\]`);
        return this._errorByRegex(regex);
    }

    _errorByRegex(regex) {
        const error = new Error();
        if (this.details) {
            this.details.forEach((detail) => {
                const matches = detail.attributeName.match(regex);
                if (matches !== null && matches[1]) {
                    error.add(matches[1], detail.constraintMessage);
                }
            });
        }
        return error;
    }
}
